import { FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useCancelTicketMutation } from '../../api/queries/ticket';
import Alert from '../../components/Alert/Alert';
import Button from '../../components/Button';
import { StepsUpdateContext } from '../../components/Steps/StepsContext';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { TicketContext } from '../../contexts/TicketContext';
import { useScrollToTop } from '../../helpers/hooks/useScrollToTop';
import { getConfirmStatuses } from '../../helpers/TicketHelpers';
import { Ticket } from '../../interfaces/Ticket';
import { sendCancelAppointmentClicked } from '../../services/Analytics';
import { useFormatting } from '../../services/hooks/useFormatting';
import usePageTitle from '../../services/hooks/usePageTitle';
import styles from './Cancel.module.scss';


const Cancel: FC = () => {
  const { ticket } = useContext(TicketContext);
  const { stylesheet } = useContext(StylesheetContext);
  const { navigateToActiveStep } = useContext(StepsUpdateContext);
  const { formattedText: cancelSectionText } = useFormatting(stylesheet.cancelSection.textBlock.value);
  const { cancelTicket, isPending, isSuccess, isError } = useCancelTicketMutation();
  const { t } = useTranslation();

  useScrollToTop();

  usePageTitle(t('pageTitle:cancel'));

  const doCancelAppointment = useCallback((ticket: Ticket) => {
    sendCancelAppointmentClicked();
    cancelTicket(ticket.workOrder.appointment.id);
  }, [cancelTicket]);

  const handleExternalNavigation = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  if (!ticket) {
    // Should never happen, ErrorElement will be rendered instead
    return null;
  }

  const isConfirmStatus = getConfirmStatuses().includes(ticket.workOrder.appointment.status);
  const { settings: { partnerName, partnerUrl } } = stylesheet;

  return (
    <div className={styles.cancelRoot}>
      <div className={styles.innerContainer}>
        <h1>{isSuccess ? t('cancel:titleSuccess') : t('cancel:title')}</h1>

        {!isSuccess && (
          isConfirmStatus ? (
            <p>{t('cancel:description', { partner: partnerName })}</p>
          ) : (
            <p>{cancelSectionText}</p>
          )
        )}

        {(!isConfirmStatus || isSuccess) && <Alert
          className={styles.alert}
          icon={!isSuccess ? 'calendar_today' : undefined}
          level="info"
          text={!isSuccess ? t('cancel:makeAppointment') : t('cancel:makeNewAppointment')}
          textButton={{
            text: !isSuccess
              ? t('cancel:makeAppointmentLink')
              : t('cancel:contactPartner', { partner: partnerName }),
            onClick: () => {
              if (!isSuccess) {
                navigateToActiveStep();
              } else {
                handleExternalNavigation(partnerUrl);
              }
            },
          }}
          white
        />}

        {!!isError && (
          <Alert
            level="error"
            text={t('cancel:errorMessage')}
          />
        )}
      </div>

      {!isSuccess && (
        <div className={styles.buttonContainer}>
          <Button
            ariaLabel={t('button:cancelAppointment')}
            disabled={isPending}
            onClick={() => doCancelAppointment(ticket)}
            text={t('button:cancelAppointment')}
          />
          <Button
            ariaLabel={t('button:abortCancelAppointment')}
            onClick={navigateToActiveStep}
            text={t('button:abortCancelAppointment')}
            variant="text"
          />
        </div>
      )}
    </div>
  );
};

export default Cancel;
